import { Inject, Injectable } from '@angular/core';
import {
	Observable,
	ReplaySubject,
	tap,
	finalize,
	catchError,
	EMPTY,
	Subscription
} from 'rxjs';
import { MediaTranscription, TranscriptionStatus } from '@go-modules/interfaces/media-transcription';
import { TranscriptionService } from '@go-modules/services/transcription/transcription.service';
import type { Media } from 'go-modules/services/attachment/media';
import {
	SelectedService,
	selectedServiceToken
} from 'go-modules/services/selected/selected.service';
import { LlmTaskStatus } from '@go-modules/interfaces/llm-task';
import { NgxFeatureFlagService } from '../feature-flag/feature-flag.service';

interface MediaTranscriptionCacheEntry {
	/** The subject that subscribers receive. Never ending stream */
	subject$: ReplaySubject<MediaTranscription>;

	/** undefined  ⇒ no data yet
	 *  false      ⇒ data fetched, analytics not included
	 *  true       ⇒ data fetched, analytics included
	 *  Helps avoid unnecessary refetches. Can't rely on empty analytics in response since it's possible for
	 *  analytics to be empty even after requesting with includeAnalytics=true
	 */
	fetchedWithAnalytics?: boolean;

	/** Internal only. Track in-flight requests to prevent duplicate API calls when waiting for the first response */
	inFlight$?: Observable<MediaTranscription>;

	/** Internal only. Track subscription to in-flight request for cancellation */
	inFlightSubscription?: Subscription;

	/** Internal only. Track what type of request is in flight */
	inFlightWithAnalytics?: boolean;
}

export interface GetTranscriptionOptions {
	media: Media;
	includeAnalytics?: boolean;
	forceRefresh?: boolean;
}

export interface CreateTranscriptionOptions {
	media: Media;
}

@Injectable({ providedIn: 'root' })
export class TranscriptCacheService {
	private cache = new Map<string, MediaTranscriptionCacheEntry>();

	constructor (
		private transcriptionService: TranscriptionService,
		private featureFlagService: NgxFeatureFlagService,
		@Inject(selectedServiceToken) private selectedService: SelectedService
	) {}

	public getTranscription ({
		media,
		includeAnalytics = false,
		forceRefresh = false
	}: GetTranscriptionOptions): Observable<MediaTranscription> {
		if (!media) throw new Error('Media is required');
		const mediaId = String(media.media_id);
		const entry = this.getOrCreateEntry(mediaId);

		const license = this.selectedService.getLicense();
		const analyticsAllowed = !!license?.salesforce_license?.transcript_analytics_enabled;
		const wantAnalytics = includeAnalytics && analyticsAllowed;

		const mustFetch =
			forceRefresh ||
			entry.fetchedWithAnalytics === undefined ||
			(wantAnalytics && entry.fetchedWithAnalytics === false);

		// If we need analytics but have an in-flight request without analytics, cancel it
		if (wantAnalytics && entry.inFlight$ && entry.inFlightWithAnalytics === false) {
			this.cancelInFlightRequest(entry);
		}

		if (mustFetch) {
			this.ensureFetch(
				entry,
				() => this.transcriptionService.getTranscription(media, wantAnalytics),
				wantAnalytics,
				mediaId
			);
		}

		return entry.subject$.asObservable();
	}

	public createTranscription ({ media}: CreateTranscriptionOptions): Observable<MediaTranscription> {
		const mediaId = String(media.media_id);
		const entry = this.getOrCreateEntry(mediaId);

		this.ensureFetch(
			entry,
			() => this.transcriptionService.createTranscription(media),
			true,
			mediaId
		);

		return entry.subject$.asObservable();
	}

	// Clear single entry or entire cache
	public clearCache (mediaId?: string): void {
		if (mediaId) {
			const entry = this.cache.get(mediaId);
			if (entry) {
				this.cancelInFlightRequest(entry);
				entry.subject$.complete();
				this.cache.delete(mediaId);
			}
		} else {
			this.cache.forEach((entry) => {
				this.cancelInFlightRequest(entry);
				entry.subject$.complete();
			});
			this.cache.clear();
		}
	}

	private getOrCreateEntry (mediaId: string): MediaTranscriptionCacheEntry {
		return this.cache.get(mediaId) ?? this.createEntry(mediaId);
	}

	private createEntry (mediaId: string): MediaTranscriptionCacheEntry {
		const out: MediaTranscriptionCacheEntry = { subject$: new ReplaySubject<MediaTranscription>(1) };
		this.cache.set(mediaId, out);
		return out;
	}

	/**
	 * Cancel an in-flight request
	 */
	private cancelInFlightRequest (entry: MediaTranscriptionCacheEntry): void {
		if (entry.inFlightSubscription) {
			entry.inFlightSubscription.unsubscribe();
			entry.inFlightSubscription = undefined;
		}
		entry.inFlight$ = undefined;
	}

	/**
	 * Start the HTTP call if not already running
	 */
	private ensureFetch (
		entry: MediaTranscriptionCacheEntry,
		httpFactory: () => Observable<MediaTranscription>,
		withAnalytics: boolean,
		mediaId: string
	): void {
		if (entry.inFlight$) return;

		entry.inFlightWithAnalytics = withAnalytics;
		entry.inFlight$ = httpFactory().pipe(
			tap((tx) => {
				// Only cache if the transcription is COMPLETED
				// Otherwise, clear the cache entry to allow future requests
				if (tx.status === TranscriptionStatus.COMPLETED) {
					if (this.featureFlagService.isAvailable('VIDEO_CHAPTERS') && tx.transcription_tasks) {
						if ([LlmTaskStatus.PROCESSING, LlmTaskStatus.QUEUED].includes(tx.transcription_tasks.status)) {
							// Allow current subscribers to receive the value
							entry.subject$.next(tx);
							// but ensures future requests will fetch fresh data
							this.clearCache(mediaId);
							return;
						}
					}
					entry.fetchedWithAnalytics = withAnalytics;
					entry.subject$.next(tx);
				} else {
					// Allow current subscribers to receive the value
					entry.subject$.next(tx);
					// but ensures future requests will fetch fresh data
					this.clearCache(mediaId);
				}
			}),
			catchError((error) => {
				// clear on error so future requests can retry
				this.cache.delete(mediaId);
				// propagate error to all subscribers via the subject
				entry.subject$.error(error);
				// complete the observable chain
				return EMPTY;
			}),
			finalize(() => {
				delete entry.inFlight$;
				delete entry.inFlightSubscription;
				delete entry.inFlightWithAnalytics;
			})
		);

		// Fire immediately so the result is cached even if no component subscribed to subject$ yet
		entry.inFlightSubscription = entry.inFlight$.subscribe();
	}
}
